import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sparkles,
  Send,
  Loader2,
  Plus,
  History,
  Trash2,
  Lock,
  MessageSquare,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import SubscribeDialog from "@/components/SubscribeDialog";

const SUGGESTIONS = [
  "¿En qué estoy gastando más este mes?",
  "Dame 3 recomendaciones para ahorrar más",
  "¿Cómo voy con mis presupuestos?",
  "¿Cuánto puedo gastar al día sin pasarme?",
];

function MessageBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`assistant-msg-${role}`}
    >
      <div
        className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
          isUser
            ? "bg-[#0052FF] text-white rounded-br-sm"
            : "bg-[#1B2638] text-[#F1F5F9] rounded-bl-sm"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

function PremiumGate({ onUpgrade }) {
  return (
    <div
      className="flex flex-col items-center justify-center text-center px-6 py-10 gap-3"
      data-testid="assistant-premium-gate"
    >
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0052FF] to-[#1E40AF] flex items-center justify-center shadow-lg">
        <Lock className="w-6 h-6 text-white" />
      </div>
      <h3 className="font-display text-xl font-semibold text-[#F1F5F9]">
        Pregúntale a Finly+
      </h3>
      <p className="text-sm text-[#64748B] max-w-xs">
        Tu copiloto financiero personal con IA. Disponible solo para usuarios
        Premium.
      </p>
      <ul className="text-xs text-[#64748B] space-y-1.5 mt-2 text-left">
        <li>• Respuestas con tus datos reales</li>
        <li>• Recomendaciones accionables al instante</li>
        <li>• Conversaciones ilimitadas</li>
      </ul>
      <Button
        onClick={onUpgrade}
        className="mt-3 bg-[#0052FF] hover:bg-[#0040CC] text-white"
        data-testid="assistant-upgrade-btn"
      >
        Desbloquear con Premium
      </Button>
    </div>
  );
}

export default function AssistantWidget() {
  const { user, subscriptionStatus } = useAuth();
  const [open, setOpen] = useState(false);
  const [openSub, setOpenSub] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const scrollRef = useRef(null);

  const isPaidPremium =
    subscriptionStatus === "active" || subscriptionStatus === "admin";

  // Don't render the widget at all if user isn't logged in.
  const hasUser = !!user && user !== false;

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/assistant/sessions");
      setSessions(data || []);
    } catch (e) {
      // 402 -> not premium; silently ignore (gate handles UI)
    }
  }, []);

  const loadMessages = useCallback(
    async (sid) => {
      if (!sid) return;
      setLoadingHistory(true);
      try {
        const { data } = await api.get("/assistant/messages", {
          params: { session_id: sid },
        });
        setMessages(data || []);
        scrollToBottom();
      } catch (e) {
        setMessages([]);
      } finally {
        setLoadingHistory(false);
      }
    },
    [scrollToBottom]
  );

  // When drawer opens for a paid premium user, fetch session list.
  useEffect(() => {
    if (!open || !isPaidPremium) return;
    loadSessions();
  }, [open, isPaidPremium, loadSessions]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
    setShowSessions(false);
    setInput("");
  };

  const pickSession = async (sid) => {
    setSessionId(sid);
    setShowSessions(false);
    await loadMessages(sid);
  };

  const deleteSession = async (sid, ev) => {
    ev?.stopPropagation();
    try {
      await api.delete(`/assistant/sessions/${sid}`);
      toast.success("Conversación eliminada");
      if (sid === sessionId) newChat();
      await loadSessions();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;

    // Optimistic user message
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      session_id: sessionId || "pending",
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setInput("");
    setSending(true);

    try {
      const { data } = await api.post("/assistant/chat", {
        message: text,
        session_id: sessionId,
      });
      // Replace the optimistic with actual session_id, append assistant reply.
      setMessages((prev) => {
        const next = prev.map((m) =>
          m.id === tempId ? { ...m, session_id: data.session_id } : m
        );
        next.push(data.message);
        return next;
      });
      if (!sessionId) {
        setSessionId(data.session_id);
        // Refresh sessions list with the new session.
        loadSessions();
      }
    } catch (e) {
      // Remove the optimistic msg so the user can retry.
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setInput(text);
      const status = e?.response?.status;
      if (status === 402) {
        toast.error("Necesitas Premium para usar el Asistente IA");
        setOpenSub(true);
      } else {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Error");
      }
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!hasUser) return null;

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Abrir asistente Finly+"
        data-testid="assistant-fab"
        className="fixed bottom-24 right-6 z-40 group h-14 w-14 rounded-full bg-gradient-to-br from-[#0052FF] to-[#1E40AF] shadow-lg shadow-[#0052FF]/30 hover:shadow-xl hover:shadow-[#0052FF]/40 hover:scale-105 active:scale-95 transition-all flex items-center justify-center ring-2 ring-white"
      >
        <Sparkles className="w-6 h-6 text-white" />
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#00E590] ring-2 ring-white" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md p-0 flex flex-col h-full z-[70]"
          data-testid="assistant-drawer"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#2A3447] space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0052FF] to-[#1E40AF] flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <SheetTitle className="font-display text-base text-[#F1F5F9] text-left">
                    Pregúntale a Finly+
                  </SheetTitle>
                  <SheetDescription className="text-[11px] text-[#64748B] text-left">
                    Tu copiloto financiero con IA
                  </SheetDescription>
                </div>
              </div>
              {isPaidPremium && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={newChat}
                    className="h-8 px-2 text-[#0052FF] hover:bg-[#0052FF]/10"
                    data-testid="assistant-new-chat-btn"
                    title="Nuevo chat"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSessions((v) => !v)}
                    className="h-8 px-2 text-[#F1F5F9] hover:bg-[#1B2638]"
                    data-testid="assistant-history-btn"
                    title="Historial"
                  >
                    <History className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </SheetHeader>

          {!isPaidPremium ? (
            <div className="flex-1 flex items-center justify-center">
              <PremiumGate onUpgrade={() => setOpenSub(true)} />
            </div>
          ) : showSessions ? (
            <div
              className="flex-1 overflow-y-auto p-4 space-y-2"
              data-testid="assistant-sessions-list"
            >
              {sessions.length === 0 ? (
                <p className="text-sm text-[#64748B] text-center py-8">
                  Aún no tienes conversaciones.
                </p>
              ) : (
                sessions.map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => pickSession(s.session_id)}
                    className="w-full text-left p-3 rounded-lg border border-[#2A3447] hover:border-[#0052FF] hover:bg-[#0052FF]/5 transition-colors group"
                    data-testid={`assistant-session-${s.session_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#F1F5F9] truncate">
                          {s.title}
                        </p>
                        <p className="text-[11px] text-[#64748B] mt-0.5">
                          {s.message_count} mensaje
                          {s.message_count === 1 ? "" : "s"} ·{" "}
                          {new Date(s.last_message_at).toLocaleDateString(
                            "es-MX",
                            { day: "2-digit", month: "short" }
                          )}
                        </p>
                      </div>
                      <span
                        onClick={(ev) => deleteSession(s.session_id, ev)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-[#64748B] hover:text-red-600 p-1 cursor-pointer"
                        data-testid={`assistant-session-delete-${s.session_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : (
            <>
              {/* Messages area */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
                data-testid="assistant-messages"
              >
                {loadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-[#0052FF]" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-6 space-y-4">
                    <div className="w-12 h-12 mx-auto rounded-2xl bg-[#0052FF]/10 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-[#0052FF]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#F1F5F9]">
                        Empieza una conversación
                      </p>
                      <p className="text-[12px] text-[#64748B] mt-1 px-6">
                        Te respondo usando tus transacciones y presupuestos
                        reales.
                      </p>
                    </div>
                    <div className="space-y-1.5 px-2">
                      {SUGGESTIONS.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => send(s)}
                          className="w-full text-left text-[12.5px] px-3 py-2 rounded-lg border border-[#2A3447] hover:border-[#0052FF] hover:bg-[#0052FF]/5 text-[#F1F5F9] transition-colors"
                          data-testid={`assistant-suggestion-${i}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map((m) => (
                    <MessageBubble
                      key={m.id}
                      role={m.role}
                      content={m.content}
                    />
                  ))
                )}
                {sending && (
                  <div className="flex justify-start" data-testid="assistant-typing">
                    <div className="bg-[#1B2638] px-3.5 py-3 rounded-2xl rounded-bl-sm flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#64748B] animate-bounce [animation-delay:-0.3s]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#64748B] animate-bounce [animation-delay:-0.15s]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#64748B] animate-bounce" />
                    </div>
                  </div>
                )}
              </div>

              {/* Input area */}
              <div className="border-t border-[#2A3447] p-3 bg-[#131C2E]">
                <div className="flex items-end gap-2">
                  <Textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Pregunta lo que quieras sobre tus finanzas…"
                    rows={1}
                    className="min-h-[42px] max-h-32 resize-none text-sm border-[#2A3447] focus-visible:ring-[#0052FF]"
                    disabled={sending}
                    data-testid="assistant-input"
                  />
                  <Button
                    onClick={() => send()}
                    disabled={sending || !input.trim()}
                    className="h-[42px] w-[42px] p-0 bg-[#0052FF] hover:bg-[#0040CC] text-white flex-shrink-0"
                    data-testid="assistant-send-btn"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-[10px] text-[#94A3B8] mt-1.5 px-1">
                  Enter para enviar · Shift + Enter para nueva línea
                </p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <SubscribeDialog open={openSub} onOpenChange={setOpenSub} />
    </>
  );
}
